import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "tab", "dropdownWrapper", "menuItem"]
  static values = {
    mobileMax: { type: Number, default: 0 },
    mobileBreakpoint: { type: Number, default: 640 },
    desktopMax: { type: Number, default: 5 },
    desktopBreakpoint: { type: Number, default: 1280 }
  }

  connect() {
    if (!this.hasContainerTarget || !this.hasDropdownWrapperTarget) return

    this.handleResize = this.layout.bind(this)
    window.addEventListener("resize", this.handleResize)

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.layout())
      this.resizeObserver.observe(this.element)
    }

    requestAnimationFrame(() => this.layout())
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.resizeObserver?.disconnect()
  }

  layout() {
    this.showAllPrimaryTabs()
    this.hideAllMenuItems()
    this.dropdownWrapperTarget.classList.add("hidden")

    if (this.shouldShowAllDesktopTabs()) return

    if (this.shouldLimitMobileTabs()) {
      this.applyMobileLimit(this.measureDropdownWidth())
      return
    }

    if (!this.hasOverflow()) return

    this.applyOverflow(this.measureDropdownWidth())
  }

  shouldLimitMobileTabs() {
    return this.mobileMaxValue > 0 &&
      this.tabTargets.length > this.mobileMaxValue &&
      window.innerWidth < this.mobileBreakpointValue
  }

  shouldShowAllDesktopTabs() {
    return this.desktopMaxValue > 0 &&
      this.tabTargets.length <= this.desktopMaxValue &&
      window.innerWidth >= this.desktopBreakpointValue
  }

  applyMobileLimit(dropdownWidth) {
    const availableWidth = this.containerTarget.clientWidth - dropdownWidth
    const gapWidth = this.containerGap()
    let usedWidth = 0

    this.tabTargets.forEach((tab, index) => {
      const tabWidth = tab.offsetWidth
      const nextWidth = usedWidth === 0 ? tabWidth : gapWidth + tabWidth
      const exceedsLimit = index >= this.mobileMaxValue
      const exceedsWidth = usedWidth + nextWidth > availableWidth

      if (exceedsLimit || exceedsWidth) {
        tab.classList.add("hidden")
        this.showMenuItemFor(tab.dataset.overflowIndex)
      } else {
        usedWidth += nextWidth
      }
    })

    this.dropdownWrapperTarget.classList.remove("hidden")
  }

  showAllPrimaryTabs() {
    this.tabTargets.forEach((tab) => tab.classList.remove("hidden"))
  }

  hideAllMenuItems() {
    this.menuItemTargets.forEach((item) => item.classList.add("hidden"))
  }

  hasOverflow() {
    return this.totalTabWidth() > this.containerTarget.clientWidth
  }

  totalTabWidth() {
    const tabsWidth = this.tabTargets.reduce((sum, tab) => sum + tab.offsetWidth, 0)
    const gapsWidth = Math.max(this.tabTargets.length - 1, 0) * this.containerGap()

    return tabsWidth + gapsWidth
  }

  containerGap() {
    return parseFloat(window.getComputedStyle(this.containerTarget).columnGap) || 0
  }

  measureDropdownWidth() {
    this.dropdownWrapperTarget.classList.remove("hidden")
    const width = this.dropdownWrapperTarget.offsetWidth || 112
    this.dropdownWrapperTarget.classList.add("hidden")

    return width
  }

  applyOverflow(dropdownWidth) {
    const availableWidth = this.containerTarget.clientWidth - dropdownWidth
    let usedWidth = 0
    let overflowed = false

    this.tabTargets.forEach((tab) => {
      const tabWidth = tab.offsetWidth

      if (usedWidth + tabWidth > availableWidth) {
        overflowed = true
        tab.classList.add("hidden")
        this.showMenuItemFor(tab.dataset.overflowIndex)
      } else {
        usedWidth += tabWidth
      }
    })

    if (overflowed) this.dropdownWrapperTarget.classList.remove("hidden")
  }

  showMenuItemFor(index) {
    const menuItem = this.menuItemTargets.find((item) => item.dataset.overflowIndex === index)
    if (menuItem) menuItem.classList.remove("hidden")
  }
}
